// client/src/components/secure/ZoningRolodex.js — the zoning rolodex, filed by town.
//
// Site-search research for placing a school: which municipality permits the use in
// which district, the rule that decides it, and the buildings, agents and owners that
// go with it. The content comes from data/zoning-rolodex.json on the server.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import Modal from './Modal';
import DataList from '../shared/DataList';
import ZoningLookupBox from './ZoningLookupBox';
import { LibraryEntryForm, ZoningQueue, MapLayers } from './ZoningLibraryAdmin';
import { CoverageMatrix, BusinessCatalog, ResearchQueue, UsesAllowed, ZoningChanges, CodeSources } from './ZoningBusinessLibrary';
import { useBusinesses } from './BusinessPicker';
import { getCurrentUser } from '../../hooks/useCurrentUser';

// The page's sections. "Library" is the town rolodex below; the rest are the Business Library
// (owner, 22 Oct 2026: any business a client needs, not just the four school uses) and the
// town use tables ("a dynamic list of uses that changes by city and its zoning, with updates").
const TABS = [
    { key: 'library', label: 'Library' },
    { key: 'coverage', label: 'Coverage' },
    { key: 'businesses', label: 'Businesses' },
    { key: 'research', label: 'Research' },
    { key: 'uses', label: 'Uses allowed' },
    { key: 'changes', label: 'Zoning changes' },
    { key: 'sources', label: 'Code sources' },
];

// The use status is the answer, so it gets the colour.
const STATUS = {
    ok: { label: 'BY RIGHT', pill: 'bg-green-100 text-green-800 border-green-200' },
    su: { label: 'SPECIAL USE', pill: 'bg-amber-100 text-amber-800 border-amber-200' },
    no: { label: 'NO', pill: 'bg-red-100 text-red-800 border-red-200' },
    ref: { label: 'REFERENCE', pill: 'bg-blue-100 text-blue-800 border-blue-200' },
};

const statusOf = (use) => {
    const u = (use || '').toLowerCase();
    if (u.startsWith('by right')) return 'ok';
    if (u.includes('special')) return 'su';
    if (u.includes('not allowed') || u.includes('excluded') || u.includes('not a listed')) return 'no';
    if (u.includes('unverified')) return 'su';
    if (u.includes('reference') || u.includes('dropped')) return 'ref';
    return 'ref';
};

const verdictTone = (verdict) => {
    const v = (verdict || '').toUpperCase();
    if (v.startsWith('BY RIGHT')) return 'border-green-500 bg-green-50';
    if (v.startsWith('SPECIAL')) return 'border-amber-500 bg-amber-50';
    if (v.startsWith('NOT')) return 'border-red-500 bg-red-50';
    return 'border-gray-400 bg-gray-50';
};

const Pill = ({ s }) => (
    <span className={`inline-block whitespace-nowrap rounded border px-2 py-0.5 text-[10px] font-semibold tracking-wide ${STATUS[s].pill}`}>
        {STATUS[s].label}
    </span>
);

const mono = (v) => <span className="font-mono text-[11px] whitespace-nowrap">{v || '—'}</span>;
const PROP_COLUMNS = [
    { key: 'addr', label: 'Address', render: (p) => <span className="font-semibold text-gray-900">{p.addr}</span> },
    { key: 'zone', label: 'Zone', render: (p) => <span className="whitespace-nowrap rounded bg-gray-100 px-2 py-0.5 font-mono text-[11px]">{p.zone || '—'}</span> },
    { key: 'status', label: 'School use', value: (p) => STATUS[statusOf(p.use)].label, render: (p) => <Pill s={statusOf(p.use)} /> },
    { key: 'sf', label: 'SF', render: (p) => mono(p.sf) },
    { key: 'rate', label: 'Rate', render: (p) => mono(p.rate) },
    { key: 'pin', label: 'PIN', render: (p) => mono(p.pin) },
    { key: 'agent', label: 'Agent', render: (p) => p.agent || '—' },
    { key: 'phone', label: 'Phone', render: (p) => (p.phone
        ? <a className="font-mono text-[11px] whitespace-nowrap text-blue-700 hover:underline" href={`tel:${p.phone.replace(/[^\d+]/g, '')}`}>{p.phone}</a>
        : '—') },
    { key: 'owner', label: 'Owner', render: (p) => <span className="text-xs text-gray-600">{p.owner || '—'}</span> },
    { key: 'notes', label: 'Notes', render: (p) => <span className="text-xs text-gray-600">{p.notes}</span> },
];
const dimRow = (p) => (['no', 'ref'].includes(statusOf(p.use)) ? 'bg-gray-50 text-gray-600' : '');
const pillFor = (v) => (v === 'by_right' ? 'ok' : v === 'special_use' ? 'su' : v === 'not_allowed' ? 'no' : 'ref');

const ZoningRolodex = () => {
    const [book, setBook] = useState(null);
    const [error, setError] = useState('');
    const [town, setTown] = useState(null);
    const [use, setUse] = useState('');
    const [verdict, setVerdict] = useState('');
    const [pivot, setPivot] = useState('town');
    const [editing, setEditing] = useState(null);   // null | {} (new) | a library row
    const [tab, setTab] = useState('library');
    const [reloadKey, setReloadKey] = useState(0);
    const apiClient = useApiClient();
    const { data: catalog } = useBusinesses();
    const user = getCurrentUser();
    const canManage = !!(user && (user.isManagingBroker || user.staffRole === 'Office Manager' || user.role === 'admin') && user.staffRole !== 'Read Only');

    const load = useCallback(async () => {
        try {
            // The library is a flat list of {municipality, use_category, ...};
            // group it by town so the page keeps its rolodex shape.
            const rows = await apiClient.get('/api/site-searches/zoning-library');
            const towns = {};
            for (const r of rows) {
                const t = (towns[r.municipality] ||= {
                    verdict: r.verdict,
                    verdict_status: r.verdict_status,
                    rules: Array.isArray(r.rules) ? r.rules : [],
                    contact: [r.official_name, r.official_title, r.official_phone || r.department_phone]
                        .filter(Boolean).join(' · '),
                    props: [],
                });
                t.props.push({
                    addr: r.use_category, zone: r.districts_by_right || '—',
                    use: r.verdict_status === 'by_right' ? 'By right'
                        : r.verdict_status === 'special_use' ? 'Special use'
                        : r.verdict_status === 'not_allowed' ? 'Not allowed' : 'Unverified',
                    sf: '', rate: '', pin: '', agent: r.official_name || '',
                    phone: r.official_phone || r.department_phone || '',
                    owner: r.verified_on ? `verified ${String(r.verified_on).slice(0, 10)}` : '',
                    notes: r.conditions || r.notes || '',
                });
            }
            setBook({ updated: new Date().toISOString().slice(0, 10),
                      purpose: 'Municipal zoning answers, researched once and shared by every client search.',
                      towns, rows });
            setTown((t) => t || Object.keys(towns)[0] || null);
            return rows;
        } catch (err) {
            setError(err.message || 'Could not load the zoning rolodex.');
        }
    }, [apiClient]);

    useEffect(() => { load(); }, [load]);

    // Open a library entry by id (from the coverage grid or the research queue); reload when a
    // research answer created it since the page was loaded.
    const openEntry = useCallback(async (jid) => {
        let row = (book?.rows || []).find((r) => Number(r.jurisdiction_id) === Number(jid));
        if (!row) { const rows = await load(); row = (rows || []).find((r) => Number(r.jurisdiction_id) === Number(jid)); }
        if (row) setEditing(row);
    }, [book, load]);
    const afterSave = () => { setEditing(null); load(); setReloadKey((k) => k + 1); };

    const uses = useMemo(
        () => [...new Set((book?.rows || []).map((r) => r.use_category))].sort(), [book]);
    const libraryRows = useMemo(() => (book?.rows || [])
        .filter((r) => (!use || r.use_category === use) && (!verdict || r.verdict_status === verdict))
        .sort((a, b) => a.municipality.localeCompare(b.municipality)), [book, use, verdict]);
    const towns = useMemo(() => Object.keys(book?.towns || {}), [book]);
    const counts = useMemo(() => {
        const out = {};
        towns.forEach((t) => {
            const props = book.towns[t].props || [];
            out[t] = { all: props.length, ok: props.filter((p) => statusOf(p.use) === 'ok').length };
        });
        return out;
    }, [book, towns]);

    const current = book && town ? book.towns[town] : null;

    if (error) {
        return (
            <div className="rounded border border-red-200 bg-red-50 p-4 text-red-800">
                <p className="font-semibold">The zoning rolodex could not be loaded.</p>
                <p className="mt-1 text-sm">{error}</p>
            </div>
        );
    }
    if (!book) return <p className="p-4 text-gray-500">Loading the zoning rolodex…</p>;

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Zoning Library</h1>
                    <p className="max-w-3xl text-sm text-gray-600">{book.purpose}</p>
                </div>
                <div className="text-right text-xs text-gray-500">
                    <p>Updated {book.updated}</p>
                    <button type="button" onClick={() => setEditing({ municipality: town || '' })} className="mt-1 mr-1 rounded bg-slate-800 px-3 py-1 text-xs font-semibold text-white print:hidden">
                        Add entry
                    </button>
                    <button type="button" onClick={() => window.print()} className="mt-1 rounded border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 print:hidden">
                        Print
                    </button>
                </div>
            </div>

            <div className="flex flex-wrap gap-1 border-b border-gray-200 print:hidden">
                {TABS.map((x) => (
                    <button key={x.key} type="button" onClick={() => setTab(x.key)}
                        className={`rounded-t px-3 py-1.5 text-sm font-semibold ${tab === x.key ? 'bg-slate-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{x.label}</button>
                ))}
            </div>

            {tab === 'coverage' && <CoverageMatrix reloadKey={reloadKey} onOpen={openEntry} onCreate={(e) => setEditing(e)} />}
            {tab === 'businesses' && <BusinessCatalog Modal={Modal} />}
            {tab === 'research' && <ResearchQueue reloadKey={reloadKey} canManage={canManage} onOpen={openEntry} />}
            {tab === 'uses' && <UsesAllowed canManage={canManage} />}
            {tab === 'changes' && <ZoningChanges reloadKey={reloadKey} canManage={canManage} onChanged={() => { load(); setReloadKey((k) => k + 1); }} />}
            {tab === 'sources' && <CodeSources Modal={Modal} canManage={canManage} />}

            {tab === 'library' && (<>
            <div className="print:hidden">
                <ZoningLookupBox uses={(catalog && catalog.uses && catalog.uses.length) ? catalog.uses : uses} />
            </div>

            <details className="rounded border border-gray-200 bg-white p-3 print:hidden">
                <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-gray-500">Zoning checks by the office AI (towns with no online map)</summary>
                <div className="mt-2"><ZoningQueue /></div>
            </details>

            <details className="rounded border border-gray-200 bg-white p-3 print:hidden">
                <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-gray-500">Online zoning maps by town</summary>
                <div className="mt-2"><MapLayers Modal={Modal} /></div>
            </details>

            <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 pb-2 text-xs print:hidden">
                <label className="font-semibold uppercase tracking-wide text-gray-500">Use</label>
                <select value={use} onChange={(e) => setUse(e.target.value)}
                        className="rounded border border-gray-300 px-2 py-1">
                    <option value="">All uses</option>
                    {uses.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
                <label className="ml-2 font-semibold uppercase tracking-wide text-gray-500">Answer</label>
                <select value={verdict} onChange={(e) => setVerdict(e.target.value)}
                        className="rounded border border-gray-300 px-2 py-1">
                    <option value="">Any</option>
                    <option value="by_right">By right</option>
                    <option value="special_use">Special use</option>
                    <option value="not_allowed">Not allowed</option>
                    <option value="unverified">Unverified</option>
                </select>
                <button type="button" onClick={() => setPivot(pivot === 'town' ? 'all' : 'town')}
                        className="ml-auto rounded border border-gray-300 px-2 py-1 text-gray-700">
                    {pivot === 'town' ? 'Show every town at once' : 'Back to town tabs'}
                </button>
            </div>

            {pivot === 'all' && (
                <div className="tablewrap border border-gray-200 bg-white p-3">
                    <DataList rows={libraryRows} rowKey={(r) => `${r.municipality}-${r.use_category}`} csvName="zoning-library"
                        defaultSort={{ key: 'municipality', dir: 'asc' }} empty="Nothing matches those filters."
                        columns={[
                            { key: 'municipality', label: 'Town', render: (r) => <span className="font-semibold text-gray-900">{r.municipality}</span> },
                            { key: 'use_category', label: 'Use', render: (r) => <span className="text-xs text-gray-600">{r.use_category}</span> },
                            { key: 'answer', label: 'Answer', value: (r) => STATUS[pillFor(r.verdict_status)].label, render: (r) => <Pill s={pillFor(r.verdict_status)} /> },
                            { key: 'districts_by_right', label: 'By right in', render: (r) => <span className="font-mono text-[11px]">{r.districts_by_right || '—'}</span> },
                            { key: 'districts_special_use', label: 'Hearing in', render: (r) => <span className="font-mono text-[11px]">{r.districts_special_use || '—'}</span> },
                            { key: 'conditions', label: 'Conditions', render: (r) => <span className="text-xs text-gray-600">{r.conditions || ''}</span> },
                            { key: 'contact', label: 'Village contact', value: (r) => [r.official_name, r.official_title, r.official_phone || r.department_phone].filter(Boolean).join(', '),
                                render: (r) => (
                                    <span className="text-xs text-gray-600">
                                        {[r.official_name, r.official_title].filter(Boolean).join(', ')}
                                        {(r.official_phone || r.department_phone) && <span className="block font-mono">{r.official_phone || r.department_phone}</span>}
                                    </span>) },
                            { key: 'edit', label: '', sortable: false, csv: false, search: false, render: (r) => (
                                <span className="print:hidden">
                                    <button type="button" onClick={() => setEditing(r)} className="rounded border border-gray-300 px-2 py-1 text-xs">Edit</button>
                                    {r.interpretation_origin === 'ai_draft' && <span className="mt-1 block text-[10px] font-semibold text-amber-700">AI draft — confirm</span>}
                                    {r.interpretation_origin === 'derived' && <span className="mt-1 block text-[10px] text-indigo-700">from use table</span>}
                                    {r.code_changed_at && <span className="mt-1 block text-[10px] font-semibold text-red-700">town code changed</span>}
                                </span>) },
                        ]} />
                </div>
            )}

            {pivot === 'town' && (
            <>
            {/* Town tabs — the rolodex dividers. */}
            <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2 print:hidden">
                {towns.map((t) => (
                    <button
                        key={t}
                        type="button"
                        onClick={() => setTown(t)}
                        className={`rounded-t px-3 py-1.5 text-sm font-semibold ${
                            t === town ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    >
                        {t}
                        <span className={`ml-2 text-xs font-normal ${t === town ? 'text-gray-300' : 'text-gray-500'}`}>
                            {counts[t].ok}/{counts[t].all}
                        </span>
                    </button>
                ))}
            </div>

            {current && (
                <>
                    <div className={`border-l-4 p-3 ${verdictTone(current.verdict)}`}>
                        <h2 className="text-lg font-bold text-gray-900">{town}</h2>
                        <p className="text-sm text-gray-700">{current.verdict}</p>
                    </div>

                    <div className="rounded border border-gray-200 bg-white p-3">
                        <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">The rule</h3>
                        <ul className="list-disc space-y-1 pl-5 text-sm text-gray-700">
                            {(current.rules || []).map((r, i) => <li key={i}>{r}</li>)}
                        </ul>
                        <p className="mt-2 text-sm text-gray-700"><span className="font-semibold">Who to ask:</span> {current.contact}</p>
                    </div>

                    <div className="rounded border border-gray-200 bg-white p-3 print:hidden">
                        <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Library entries for {town}</h3>
                        <ul className="space-y-1 text-sm">
                            {(book.rows || []).filter((r) => r.municipality === town).map((r) => (
                                <li key={r.jurisdiction_id} className="flex flex-wrap items-center gap-2">
                                    <span className="font-semibold">{r.use_category}</span>
                                    <span className="text-xs text-gray-600">{r.verdict}</span>
                                    {r.citation && <span className="text-xs text-gray-500">({r.citation})</span>}
                                    {r.interpretation_origin === 'ai_draft' && <span className="text-[10px] font-semibold text-amber-700">AI draft — confirm</span>}
                                    {r.interpretation_origin === 'derived' && <span className="text-[10px] text-indigo-700">from the town's use table</span>}
                                    {r.code_changed_at && <span className="text-[10px] font-semibold text-red-700">town code changed — re-check</span>}
                                    <span className="text-[11px] text-gray-500">{r.verified_on ? `verified ${String(r.verified_on).slice(0, 10)}${r.verified_by ? ` by ${r.verified_by}` : ''}` : 'not verified'}</span>
                                    <button type="button" onClick={() => setEditing(r)} className="ml-auto rounded border border-gray-300 px-2 py-0.5 text-xs">Edit</button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="rounded border border-gray-200 bg-white p-3">
                        <DataList key={town} rows={current.props || []} rowKey={(p) => p.addr} csvName={`zoning-${town}`} columns={PROP_COLUMNS}
                            defaultSort={{ key: 'addr', dir: 'asc' }} rowClass={dimRow} empty="Nothing filed for this town yet."
                            filters={[{ key: 'status', label: 'School use' }]} />
                    </div>
                </>
            )}

            </>
            )}

            </>)}

            <Modal open={!!editing} onClose={() => setEditing(null)} size="lg"
                   title={editing?.jurisdiction_id ? `Edit: ${editing.municipality} — ${editing.use_category}` : 'Add a Zoning Library entry'}>
                {editing && <LibraryEntryForm entry={editing.jurisdiction_id ? editing : { ...editing }}
                    onDone={afterSave} onCancel={() => setEditing(null)} />}
            </Modal>

            <div className="space-y-1 text-xs text-gray-500">
                <p><span className="font-semibold">By right</span> means the code permits the use in that district outright.
                   <span className="font-semibold"> Special use</span> means a public hearing and a board vote.
                   <span className="font-semibold"> No</span> means the use is not listed, so there is nothing to apply for.</p>
                <p>Owner is the county taxpayer of record, not necessarily the name on the deed. Sizes, rates and
                   availability come from listing sites and go stale — confirm on the call. Planned development
                   overlays were not read.</p>
            </div>
        </div>
    );
};

export default ZoningRolodex;
